import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Lock, ShieldAlert } from "lucide-react";

/**
 * Password-protected confirmation dialog.
 * onConfirm(password) must be async and throw on failure (e.g. bad password → 403).
 */
export default function DeleteConfirmDialog({
  open,
  onOpenChange,
  title = "Confirmar exclusão",
  description = "Esta ação é permanente. Digite a senha de administrador para continuar.",
  itemLabel = "",
  onConfirm,
}) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setPassword("");
      setError("");
      setLoading(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) {
      setError("Digite a senha");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await onConfirm(password);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) {
        setError(detail || "Senha de exclusão inválida");
      } else if (status === 401) {
        setError("Sessão de admin expirada. Faça login novamente.");
      } else {
        setError(detail || "Erro ao excluir. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-[#12141D] border-white/10"
        data-testid="delete-confirm-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase text-white flex items-center gap-2">
            <ShieldAlert size={22} style={{ color: "#F59E0B" }} />
            {title}
          </DialogTitle>
          <div className="text-white/70 text-sm">{description}</div>
          {itemLabel ? (
            <div
              className="mt-2 border border-white/10 bg-[#0B0D14] px-3 py-2 text-white/90 text-sm truncate"
              data-testid="delete-item-label"
            >
              {itemLabel}
            </div>
          ) : null}
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4" data-testid="delete-confirm-form">
          <div>
            <Label className="label-caps block mb-2">Senha de exclusão</Label>
            <div className="relative">
              <Lock
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
              />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#0B0D14] border-white/10 focus:border-[#00E5FF] rounded-sm h-11 pl-9"
                placeholder="Digite a senha de admin"
                data-testid="delete-password-input"
                autoFocus
                autoComplete="off"
              />
            </div>
            {error ? (
              <div
                className="mt-2 text-sm"
                style={{ color: "#F87171" }}
                data-testid="delete-error"
              >
                {error}
              </div>
            ) : null}
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="border border-white/15 text-white px-5 py-2.5 text-xs uppercase tracking-widest hover:border-white transition-colors"
              data-testid="delete-cancel-btn"
            >
              Cancelar
            </button>
            <Button
              type="submit"
              disabled={loading}
              className="h-10 px-6 text-xs uppercase tracking-widest font-bold text-white rounded-sm"
              style={{ background: "#DC2626" }}
              data-testid="delete-confirm-btn"
            >
              {loading ? "Excluindo..." : "Excluir"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
